import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, Calendar, Tag, ShieldCheck, PhoneCall, HelpCircle, ChevronDown, Zap } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Reveal } from "../components/Reveal";
import SEO from "../components/SEO";
import { getBlogPost, getSimilarBlogs } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

const MarkdownComponents = {
  table: ({ node, ...props }) => (
    <div className="my-10 w-full overflow-x-auto rounded-sm border border-black/15 bg-background shadow-sm">
      <table className="w-full min-w-[640px] border-collapse text-left font-display text-sm md:text-base" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => (
    <thead className="border-b-2 border-black/20 bg-secondary/90 text-xs font-bold uppercase tracking-wider text-foreground" {...props} />
  ),
  th: ({ node, ...props }) => (
    <th className="border-r border-black/10 px-6 py-4 font-bold text-foreground last:border-r-0" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="border-b border-r border-black/10 px-6 py-4 align-top font-normal text-foreground/85 last:border-r-0" {...props} />
  ),
  tr: ({ node, ...props }) => (
    <tr className="transition-colors hover:bg-secondary/40 even:bg-secondary/20 last:border-b-0" {...props} />
  ),
  a: ({ node, href, children, ...props }) => {
    const isExternal = href && (href.startsWith("http://") || href.startsWith("https://") || href.startsWith("//"));
    return (
      <a
        href={href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        className="inline-flex items-center gap-1 font-semibold text-primary underline decoration-primary/40 underline-offset-4 transition-all duration-200 hover:decoration-primary"
        {...props}
      >
        <span>{children}</span>
        {isExternal && <ArrowUpRight className="inline-block h-4 w-4 shrink-0 transition-transform duration-200 hover:rotate-45" />}
      </a>
    );
  },
  h1: ({ node, ...props }) => (
    <h1 className="mb-6 mt-14 font-display text-3xl font-bold tracking-tight text-foreground md:text-5xl" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="mb-5 mt-12 border-b border-black/10 pb-3 font-display text-2xl font-bold tracking-tight text-foreground md:text-4xl" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="mb-4 mt-10 font-display text-xl font-bold tracking-tight text-foreground md:text-3xl" {...props} />
  ),
  h4: ({ node, ...props }) => (
    <h4 className="mb-3 mt-8 font-display text-lg font-semibold text-foreground md:text-2xl" {...props} />
  ),
  p: ({ node, ...props }) => (
    <p className="my-6 text-lg font-normal leading-relaxed text-foreground/85 md:text-xl" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="my-6 list-disc space-y-3 pl-8 text-lg leading-relaxed text-foreground/85 marker:text-primary md:text-xl" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="my-6 list-decimal space-y-3 pl-8 text-lg leading-relaxed text-foreground/85 marker:font-bold marker:text-primary md:text-xl" {...props} />
  ),
  li: ({ node, ...props }) => (
    <li className="leading-relaxed" {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote className="my-8 rounded-r border-l-4 border-primary bg-secondary/40 py-4 pl-6 pr-6 italic text-foreground/95 md:text-xl" {...props} />
  ),
  hr: ({ node, ...props }) => (
    <hr className="my-12 border-0 border-t border-black/15" {...props} />
  ),
  code: ({ node, inline, className, children, ...props }) => {
    if (inline) {
      return (
        <code className="rounded bg-secondary px-2 py-0.5 font-mono text-sm font-semibold text-primary" {...props}>
          {children}
        </code>
      );
    }
    return (
      <div className="my-8 overflow-x-auto rounded bg-foreground p-6 font-mono text-sm text-background">
        <code {...props}>{children}</code>
      </div>
    );
  },
  img: ({ node, src, alt, ...props }) => (
    <span className="my-10 block overflow-hidden rounded-sm border border-black/10 shadow-sm">
      <img src={src} alt={alt || "Article illustration"} className="h-auto w-full object-cover" {...props} />
      {alt && <span className="block bg-secondary/50 px-4 py-2.5 text-center text-xs text-muted-foreground">{alt}</span>}
    </span>
  ),
};

export default function BlogPost() {
  const { t, lang } = useLanguage();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [similarPosts, setSimilarPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setLoading(true);
    getBlogPost(slug)
      .then((data) => {
        setPost(data);
        if (data) {
          getSimilarBlogs(slug)
            .then((sim) => setSimilarPosts(Array.isArray(sim) ? sim : []))
            .catch(() => setSimilarPosts([]));
        }
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-20" data-testid="blogpost-loading">
        <span className="label animate-pulse text-muted-foreground">{t("blog.loading")}</span>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 pt-20" data-testid="blogpost-not-found">
        <SEO
          title="Article Not Found | Ashlar Buildchem"
          description="The requested article could not be found."
          canonical={`/blog/${slug}`}
        />
        <h1 className="font-display text-4xl">{t("blog.notFound")}</h1>
        <Link to="/blog" className="label text-primary">{t("blog.back")}</Link>
      </div>
    );
  }

  const postSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": post.meta_title || post.title,
        "description": post.meta_description || post.excerpt,
        "image": post.image?.startsWith("http")
          ? post.image
          : `https://www.ashlarbuildchem.com${post.image?.startsWith("/") ? "" : "/"}${post.image || "logo.png"}`,
        "datePublished": post.date,
        "publisher": {
          "@type": "Organization",
          "name": "Ashlar Buildchem",
          "logo": "https://www.ashlarbuildchem.com/logo.png"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.ashlarbuildchem.com/blog/${post.slug}`
        }
      },
      ...(post.faqs && post.faqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "mainEntity": post.faqs.map((faq) => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            }
          ]
        : [])
    ]
  };

  return (
    <div className="pt-20" data-testid="blogpost-page">
      <SEO
        title={`${post.meta_title || post.title} | Ashlar`}
        description={post.meta_description || post.excerpt}
        keywords={post.meta_keywords || `${post.category}, construction chemical, ${post.title}, Ashlar Buildchem`}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.image}
        schema={postSchema}
      />
      {/* Header section matching site width */}
      <section className="mx-auto max-w-[1600px] px-6 pb-12 pt-12 md:px-10 md:pb-16 md:pt-16">
        <Link to="/blog" className="group inline-flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-to-blog">
          <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
          <span className="label">{t("blog.journal")}</span>
        </Link>

        <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <span className="label text-primary">{post.category}</span>
          {post.read_time && (
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Clock className="h-3.5 w-3.5 text-primary" />
              {post.read_time}
            </span>
          )}
          {post.date && (
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Calendar className="h-3.5 w-3.5 text-primary" />
              {post.date}
            </span>
          )}
        </div>

        <h1
          className={`mt-6 font-display text-4xl font-bold md:text-6xl lg:text-7xl ${
            lang === "hi"
              ? "normal-case leading-[1.15] tracking-normal"
              : "uppercase leading-[0.92] tracking-tighter"
          }`}
        >
          {post.title}
        </h1>
      </section>

      {/* Hero Cover Image */}
      {post.image && (
        <section className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="clip-corner-lg overflow-hidden border border-black/10">
            <img src={post.image} alt={post.title} className="h-80 w-full object-cover md:h-[580px]" />
          </div>
        </section>
      )}

      {/* Article Content & Sidebar */}
      <section className="mx-auto max-w-[1600px] px-6 py-16 md:px-10 md:py-24">
        <div className="grid gap-16 lg:grid-cols-[1fr_360px]">
          {/* Main Article Content */}
          <main className="min-w-0">
            {/* Quick Answer / TL;DR Section */}
            {post.quick_answer && (
              <Reveal>
                <div
                  className="mb-12 rounded-sm border-l-4 border-primary bg-secondary/40 p-6 md:p-8 shadow-sm backdrop-blur-sm"
                  data-testid="blogpost-quick-answer"
                >
                  <div className="flex items-center gap-2.5 text-primary">
                    <Zap className="h-5 w-5 fill-primary/20 text-primary" />
                    <span className="label font-mono text-xs tracking-wider uppercase">
                      {t("blog.quickAnswer")}
                    </span>
                  </div>
                  <p className="mt-3.5 text-base md:text-lg font-medium leading-relaxed text-foreground/95">
                    {post.quick_answer}
                  </p>
                </div>
              </Reveal>
            )}

            <Reveal>
              <div className="markdown-content max-w-none text-foreground/90">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MarkdownComponents}>
                  {post.content || ""}
                </ReactMarkdown>
              </div>
            </Reveal>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-14 flex flex-wrap items-center gap-2.5 border-t border-black/10 pt-8" data-testid="blogpost-tags">
                <Tag className="h-4 w-4 text-muted-foreground" />
                {post.tags.map((tag, idx) => (
                  <span key={idx} className="rounded-sm bg-secondary px-3.5 py-1.5 text-xs font-semibold text-foreground/80">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* FAQs Accordion Section */}
            {post.faqs && post.faqs.length > 0 && (
              <Reveal>
                <div className="mt-16 border-t border-black/10 pt-12" data-testid="blogpost-faqs-section">
                  <div className="mb-8 flex items-start gap-3">
                    <div className="rounded-sm bg-primary/10 p-2 text-primary">
                      <HelpCircle className="h-5 w-5" />
                    </div>
                    <div>
                      <span className="label text-xs text-primary">{t("blog.faqsSubtitle")}</span>
                      <h2 className="mt-1 font-display text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                        {t("blog.faqsTitle")}
                      </h2>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {post.faqs.map((faq, idx) => {
                      const isOpen = openFaq === idx;
                      return (
                        <div
                          key={idx}
                          data-testid={`faq-item-${idx}`}
                          className="overflow-hidden rounded-sm border border-black/10 bg-secondary/20 transition-colors hover:border-black/20"
                        >
                          <button
                            type="button"
                            onClick={() => setOpenFaq(isOpen ? null : idx)}
                            className="flex w-full items-center justify-between gap-4 p-6 text-left font-display text-base font-bold text-foreground md:text-lg transition-colors hover:text-primary"
                            aria-expanded={isOpen}
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-xs font-semibold text-primary">
                                {String(idx + 1).padStart(2, "0")}
                              </span>
                              <span>{faq.question}</span>
                            </div>
                            <ChevronDown
                              className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 ${
                                isOpen ? "rotate-180 text-primary" : ""
                              }`}
                            />
                          </button>
                          {isOpen && (
                            <div className="border-t border-black/10 px-6 pb-6 pt-4 text-base leading-relaxed text-foreground/85">
                              <p>{faq.answer}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Reveal>
            )}

            {/* Specialist CTA */}
            <div className="mt-14 border-t border-black/10 pt-10">
              <Link to="/contact" data-testid="blogpost-cta" className="group inline-flex items-center gap-3 bg-primary px-10 py-5 text-primary-foreground transition-all duration-200 hover:bg-foreground hover:text-background shadow-sm">
                <span className="label">{t("blog.talkSpecialist")}</span>
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
              </Link>
            </div>
          </main>

          {/* Sticky Consultation Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8 rounded-sm border border-black/10 bg-secondary/30 p-8">
              <span className="label text-primary">{t("blog.needGuidance")}</span>
              <h3 className="font-display text-2xl font-bold leading-tight">
                {t("blog.consultSpecialists")}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {t("blog.guidanceDesc")}
              </p>
              <Link
                to="/contact"
                className="group flex w-full items-center justify-between bg-primary px-6 py-4 text-primary-foreground transition-colors hover:bg-foreground hover:text-background"
              >
                <span className="label">{t("blog.requestAdvice")}</span>
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
              </Link>
              <div className="border-t border-black/10 pt-6">
                <span className="label block text-xs text-muted-foreground">{t("blog.tollFreeSupport")}</span>
                <a href="tel:18003090808" className="mt-2 block font-display text-xl font-bold text-foreground transition-colors hover:text-primary">
                  1800 309 0808
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Similar / Related Blogs Section */}
      {similarPosts && similarPosts.length > 0 && (
        <section className="mx-auto max-w-[1600px] border-t border-black/10 px-6 py-20 md:px-10" data-testid="similar-blogs-section">
          <div className="mb-10">
            <span className="label text-primary">{t("blog.similarEyebrow") || "Explore More"}</span>
            <h2 className="mt-2 font-display text-3xl font-bold uppercase tracking-tight md:text-6xl">
              {t("blog.similarTitle") || "Similar Articles"}
            </h2>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {similarPosts.map((sp) => (
              <Link
                key={sp.slug}
                to={`/blog/${sp.slug}`}
                data-testid={`similar-blog-${sp.slug}`}
                className="group flex flex-col rounded-sm border border-black/5 bg-secondary/30 p-8 transition-all duration-300 hover:border-black/15 hover:bg-secondary/60 hover:shadow-md"
              >
                <div className="mb-6 overflow-hidden rounded-sm">
                  <img
                    src={sp.image}
                    alt={sp.title}
                    className="h-52 w-full object-cover grayscale transition-all duration-500 group-hover:scale-105 group-hover:grayscale-0"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="label text-xs text-primary">{sp.category}</span>
                  <span className="text-xs text-muted-foreground">{sp.read_time}</span>
                </div>
                <h3 className="mt-4 font-display text-2xl font-bold leading-tight line-clamp-2">{sp.title}</h3>
                <p className="mt-3 flex-1 text-sm text-muted-foreground line-clamp-2 leading-relaxed">{sp.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold text-primary">
                  <span className="label">{t("blog.readArticle") || "Read article"}</span>
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-45" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}



